import * as React from "react";
import { Input, Select, Flex, Text, Button, Divider, Icon, Alert } from "@theorgchartdev/storybook";
import { createSelectOptions } from "../../../../../../Utils/Misc";
import { CommonSelectProperties, BaseConcatenateSchema } from "./Constants";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { appstr } from "@coreapp/utils/appstr";
import {
  getFieldClassTypeFromGroup,
  removeOrganizedFieldOptionSuffix,
  getOrganizedFieldOptionValue
} from "./Utils";
import FieldTypePill from "./FieldTypePill";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";

export const ConcatenateSettings = ({
  initialValues,
  onUpdate,
  fields,
  organizedFieldOptions,
  referenceTables,
  chartConfig,
  errors,
  dataTestIdPrefix
}) => {
  const values = initialValues?.values ?? [];

  const onUpdateValues = newValues => {
    onUpdate({
      type: "concatenate",
      values: newValues
    });
  };

  return (
    <Flex direction={"column"} gap={"12px"} width={"100%"}>
      {!!errors?.value && (
        <Alert severity="error" withBorder showIcon>
          {errors.value}
        </Alert>
      )}
      {values.map((valueObject, i) => {
        return (
          <Flex
            gap={"8px"}
            direction={"column"}
            width={"100%"}
            justifyContent={"space-between"}
            key={i}
          >
            <EditableItemGroup
              key={i}
              actions={[
                {
                  dataTestId: `${dataTestIdPrefix}-value-${i}-delete`,
                  iconName: "trash-can",
                  iconProps: { color: "error" },
                  onClick: () => {
                    const updatedValues = [...values];
                    updatedValues.splice(i, 1);
                    onUpdateValues(updatedValues);
                  }
                }
              ]}
              buttonsInHierarchy
            >
              <ConcatenateItem
                key={"concatenate-item-" + i}
                idx={i}
                initialValues={valueObject}
                fields={fields}
                organizedFieldOptions={organizedFieldOptions}
                referenceTables={referenceTables}
                chartConfig={chartConfig}
                dataTestIdPrefix={dataTestIdPrefix}
                onUpdate={vo => {
                  const valuesCopy = [...values];
                  valuesCopy[i] = vo;
                  onUpdateValues(valuesCopy);
                }}
              />
            </EditableItemGroup>
            <Divider />
          </Flex>
        );
      })}
      <span>
        <Button
          onClick={() => {
            const updatedValues = [...values, BaseConcatenateSchema];
            onUpdateValues(updatedValues);
          }}
          size={"small"}
          modifier={"secondary"}
          className={"oc-special-secondary"}
          iconProps={{ name: "plus-solid" }}
          dataTestId={"concat-add-value"}
        >
          {appstr("Add new value")}
        </Button>
      </span>
    </Flex>
  );
};

const ConcatenateTypes = ["lookup", "string", "date", "referencetable"];
const ConcatenateTypeDisplayStrings = {
  lookup: appstr("Field lookup"),
  string: appstr("Text"),
  date: appstr("Date"),
  referencetable: appstr("Reference Table")
};

const ConcatenateItem = ({
  idx,
  initialValues,
  onUpdate,
  fields,
  organizedFieldOptions,
  referenceTables,
  chartConfig,
  dataTestIdPrefix
}) => {
  const type = React.useMemo(() => initialValues.type || "string");
  const value = React.useMemo(() => initialValues.value || "");

  const fieldClassType = React.useMemo(() => {
    if (initialValues.fieldClassType) return initialValues.fieldClassType;
    if (type !== "lookup" || !value || !organizedFieldOptions) return null;

    for (const group of organizedFieldOptions) {
      const fieldExists = group.options?.some(option => option.value === value);
      if (fieldExists) {
        return getFieldClassTypeFromGroup(group.value);
      }
    }
    return null;
  }, [initialValues, organizedFieldOptions]);

  return (
    <Flex width={"100%"} gap={"12px"} direction={"column"}>
      <Flex gap={"8px"} direction={"column"}>
        <Text size={"small"} modifier={"medium"}>
          {appstr("Type")}
        </Text>
        <Select
          className={"ocn-list-fixed-position"}
          id={"concatenate-item-type"}
          dataTestId={`${dataTestIdPrefix}-type-select`}
          value={type}
          placeholder={type}
          options={ConcatenateTypes.map(ct => ({
            label: ConcatenateTypeDisplayStrings[ct],
            value: ct,
            clickHandler: () => {
              onUpdate({ type: ct, value: "" });
            }
          }))}
          {...CommonSelectProperties}
        />
      </Flex>
      {
        {
          lookup: (
            <Select
              id={"concatenate-item-field-value"}
              dataTestId={`${dataTestIdPrefix}-lookup-field`}
              value={
                organizedFieldOptions && IsPositionChart(chartConfig)
                  ? getOrganizedFieldOptionValue(value, fieldClassType)
                  : value
              }
              placeholder={appstr("None")}
              listProps={FieldSelectListProps()}
              options={
                organizedFieldOptions && IsPositionChart(chartConfig)
                  ? organizedFieldOptions.map(group => ({
                      ...group,
                      options: group.options?.map(option => ({
                        ...option,
                        clickHandler: () => {
                          const newFieldClassType = getFieldClassTypeFromGroup(group.value);
                          const fieldValue = removeOrganizedFieldOptionSuffix(option.value);

                          const updateData = {
                            type,
                            value: fieldValue
                          };
                          if (newFieldClassType) {
                            updateData.fieldClassType = newFieldClassType;
                          }
                          onUpdate(updateData);
                        }
                      }))
                    }))
                  : createSelectOptions(fields, v => {
                      onUpdate({
                        type,
                        value: v
                      });
                    })
              }
              rightContent={
                organizedFieldOptions &&
                IsPositionChart(chartConfig) &&
                value &&
                fieldClassType && <FieldTypePill type={fieldClassType} />
              }
              inputProps={{
                "data-testid": "concatenate-item-field-value"
              }}
              {...CommonSelectProperties}
            />
          ),
          string: (
            <Input
              id={"concatenate-item-text-value"}
              dataTestId={`${dataTestIdPrefix}-string-value`}
              type={"text"}
              size={"small"}
              value={value}
              onChange={event => {
                onUpdate({
                  type,
                  value: event.target.value
                });
              }}
            />
          ),
          date: <div />, // no settings for the Date type
          referencetable:
            referenceTables && referenceTables.length > 0 ? (
              <Select
                id={"concatenate-item-reference-table-value"}
                dataTestId={`${dataTestIdPrefix}-reference-table`}
                value={value}
                placeholder={value}
                options={createSelectOptions(referenceTables, v => {
                  onUpdate({
                    type,
                    value: v
                  });
                })}
                {...CommonSelectProperties}
              />
            ) : (
              <Text size={"small"}>{appstr("There are no reference tables")}</Text>
            )
        }[type]
      }
    </Flex>
  );
};
